import {
    Component,
    Input,
    OnChanges,
    OnInit,
    Output,
    SimpleChanges,
    ViewEncapsulation,
    EventEmitter,
    DestroyRef, inject, HostListener
} from '@angular/core';
import {FormBuilder, FormControl, FormGroup, Validators} from '@angular/forms';
import {BehaviorSubject} from 'rxjs';
import {filter, map, take, tap} from 'rxjs/operators';
import * as RA from 'ramda-adjunct';
import { PinOverride } from '@emm/core/models/pin-override.model';
import { MatDialog } from '@angular/material/dialog';
import {
    ConfirmRestorePinsDialogComponent
} from '@emm/shared/dialogs/confirm-restore-pins-dialog/confirm-restore-pins-dialog/confirm-restore-pins-dialog.component';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {PinOverrideGroup} from '@emm/core/models/pin-override-group.model';
import {AuthFacadeService} from '@emm/auth/services/auth-facade.service';
import {ManagementApiService} from '@emm/management/services/management-api.service';
import {NotificationService} from '@emm/shared/services/notification.service';
import {MenuDateUtil} from '@emm/core/util/menu-date.util';
import {LocationSet} from '@emm/core/models/location-set.model';
import {API_ERRORS} from "@emm/core/util/api-constants";

@Component({
    encapsulation: ViewEncapsulation.None,
    selector: 'emm-pin-overrides-form',
    templateUrl: './pin-overrides-form.component.html',
    styleUrls: ['./pin-overrides-form.component.scss'],
    standalone: false
})
export class PinOverridesFormComponent implements OnInit, OnChanges {

    @Input() selected: BehaviorSubject<PinOverrideGroup>;
    @Input() managementFacade: any;
    @Input() next: any;
    @Input() getPinOverrides: any;
    @Input() locationSetTypes$: any;
    @Input() isSuperAdmin: boolean;
    @Input() isAdmin: boolean;
    canEdit: boolean;
    locationSets$ = new BehaviorSubject<LocationSet[]>(null)

    @Output() isRestoreVisible = new EventEmitter<boolean>();
    @Output() areEditButtonsVisible = new EventEmitter<boolean>();
    @Output() invalidRmi = new EventEmitter<boolean>();

    private readonly destroyRef: DestroyRef = inject(DestroyRef);
    form: FormGroup;
    tomorrow: string;
    pinOverrideDetail: PinOverrideGroup;
    // @ts-ignore
    pinOverrideDetail$ = new BehaviorSubject(this.pinOverrideDetail);
    isReadOnly$ = this.authFacade.isReadOnly$;
    pinOverrideGroup: PinOverrideGroup;
    lastOverride: number;

    enableButtons: boolean;
    backButtonClicked = false;
    today = new Date();
    minEndDate = new BehaviorSubject<Date>(null);
    selectedLocationSetType: string;

    model$ = this.pinOverrideDetail$.pipe(
        filter(RA.isNotNil),
        map((detail: any) => ({
            id: detail.id,
            location: detail.location,
            locationSetType: detail.locationSetType,
            name: detail.name,
            description: detail.description,
            effectiveDate: detail.effectiveDate,
            endDate: detail.endDate,
            pinOverrides: detail.pinOverrides.filter(result => (result.rmi > 0 &&
                (result.primaryOverride > 0 || result.secondaryOverride > 0 || result.wholeProductOverride > 0 ||
                    result.samplingDistributionOverride > 0))),
        })),
        tap((model) => {
            this.pinOverrideGroup = model as PinOverrideGroup;
            this.pinOverrideGroup.endDate = this.pinOverrideGroup.endDate ? this.pinOverrideGroup.endDate : null;
            this.lastOverride = (this.pinOverrideGroup.pinOverrides.length - 1);
            const effectiveDate = new Date(this.pinOverrideGroup.effectiveDate);
            const effectiveDatePlus1 = new Date(effectiveDate);
            effectiveDatePlus1.setDate(effectiveDate.getDate() + 1);
            this.minEndDate.next(effectiveDatePlus1);
            this.dateValidations(this.pinOverrideGroup).then();
        }),
    );

    @HostListener('window:beforeunload', ['$event'])
    handleBeforeUnload(event: Event): void {
        const hasChanges = this.hasChanges().valueOf();
        if (hasChanges) {
            event.returnValue = false;
        }
    }

    constructor(
        private authFacade: AuthFacadeService,
        private managementApiService: ManagementApiService,
        private notificationService: NotificationService,
        private dialog: MatDialog,
    ) {
    }


    ngOnInit(): void {
        this.canEdit = this.isSuperAdmin || this.isAdmin;
        this.model$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(result => {
            this.selectedLocationSetType = this.form?.controls['locationSetType'].value;
            if (this.selectedLocationSetType) {
                this.managementApiService.getAllLocationSetsByTag(this.selectedLocationSetType).pipe(take(1))
                    .subscribe((result) => this.locationSets$.next(result));
            }
        });
        this.selected.subscribe((selected) => {
            if (selected) {
                this.getData();
            }
        });
    }


    ngOnChanges(changes: SimpleChanges) {
        if (changes['selected'] && this.selected?.getValue()) {
            this.getData();
        }
    }

    getData(): void {
        this.managementApiService.getPinOverridesGroup(+this.selected?.getValue()?.id)
            .pipe(take(1))
            .subscribe(response => {
            this.pinOverrideDetail$.next(response);
        });
    }

    hasChanges(): boolean {
        if (this.enableButtons) {
            return this.enableButtons;
        } else {
            return this.form && this.form.dirty;
        }
    }


    isFormReady(): boolean {
        return this.form && this.form.dirty && !this.form.invalid;
    }

    addRow(): void {
        this.enableButtons = true;
        this.pinOverrideGroup.pinOverrides.unshift({
            id: null,
            rmi: null,
            primaryOverride: null,
            secondaryOverride: null,
            wholeProductOverride: null,
            samplingDistributionOverride: null,
        });
    }

    editLocation(): void {
        this.selectedLocationSetType = this.form.controls['locationSetType'].value;
        this.enableButtons = true;
        this.form.controls['locationSetType'].valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(locationSetType => {
            this.form.controls['location'].setValue('');
            return this.managementApiService.getAllLocationSetsByTag(locationSetType)
                .pipe(take(1))
                .subscribe((result) => this.locationSets$.next(result));
        });
    }

    editPins(): void {
        this.form.markAsDirty();
        this.enableButtons = true;
    }

    cancel(): void {
        this.enableButtons = false;
        this.form.reset(this.pinOverrideGroup);
        const oldPinOverrideGroup = this.selected.getValue();
        oldPinOverrideGroup.pinOverrides = this.pinOverrideGroup.pinOverrides;
        this.selected.next(oldPinOverrideGroup);
    }

    updateOverrides(pinOverrideGroup: PinOverrideGroup) {
        for (let i = 0; i < pinOverrideGroup.pinOverrides.length; i++) {
            pinOverrideGroup.pinOverrides[i].rmi = Number((document.getElementById('rmi' + i) as HTMLInputElement).value);
            pinOverrideGroup.pinOverrides[i].primaryOverride = Number((document.getElementById('primary' + i) as HTMLInputElement).value);
                       pinOverrideGroup.pinOverrides[i].secondaryOverride = Number((document.getElementById('secondary' + i) as HTMLInputElement).value);
                       pinOverrideGroup.pinOverrides[i].wholeProductOverride = Number((document.getElementById('whole' + i) as HTMLInputElement).value);
                       pinOverrideGroup.pinOverrides[i].samplingDistributionOverride = Number((document.getElementById('sampling' + i) as HTMLInputElement).value);

            if (pinOverrideGroup.pinOverrides[i].rmi === 0) {
                this.form.setErrors({invalid: true});
                return null;

            } else {
                this.form.setErrors(null);
            }
        }

               for (let i = 0; i < pinOverrideGroup.pinOverrides.length; i++) {
            if (pinOverrideGroup.pinOverrides[i].primaryOverride === 0) {
                pinOverrideGroup.pinOverrides[i].primaryOverride = null;
            }
            if (pinOverrideGroup.pinOverrides[i].secondaryOverride === 0) {
                pinOverrideGroup.pinOverrides[i].secondaryOverride = null;
            }
            if (pinOverrideGroup.pinOverrides[i].wholeProductOverride === 0) {
                pinOverrideGroup.pinOverrides[i].wholeProductOverride = null;
            }
            if (pinOverrideGroup.pinOverrides[i].samplingDistributionOverride === 0) {
                pinOverrideGroup.pinOverrides[i].samplingDistributionOverride = null;
            }
        }

        return pinOverrideGroup.pinOverrides;
    }

    validateRmis() {
        for (let i = 0; i < this.pinOverrideGroup.pinOverrides.length; i++) {
            const currentRmi = Number((document.getElementById('rmi' + i) as HTMLInputElement).value);

            if (currentRmi === 0) {
                this.form.setErrors({invalid: true});
                this.invalidRmi.emit(true);
                break;
            } else {
                this.form.setErrors(null);
                this.invalidRmi.emit(false);
            }
        }
    }

    updatePinOverrideGroup(pinOverrideGroup: PinOverrideGroup) {
        this.pinOverrideGroup.name = this.form.controls['name'].value;
        this.pinOverrideGroup.description = this.form.controls['description'].value;
        this.pinOverrideGroup.effectiveDate = this.form.controls['effectiveDate'].value;
        this.pinOverrideGroup.endDate = this.form.controls['endDate'].value;
        this.pinOverrideGroup.locationSetType = this.form.controls['locationSetType'].value;
        this.pinOverrideGroup.location = this.form.controls['location'].value;
    }

    removePinOverrideAtIndex(rowIndex): void {
        const overrideId = this.pinOverrideGroup.pinOverrides[rowIndex].id;

        // do an actual delete if override exists
        if(overrideId !== null){
            this.managementApiService.deletePinOverrideById(overrideId)
                .pipe(take(1))
                .subscribe();
        }

        // Remove row from UI no matter if it was an actual delete or not
        this.pinOverrideGroup.pinOverrides.splice(rowIndex, 1);
    }

    getRemoveButtonToolTip(overrideExists): string {
        if(overrideExists){
            return 'Delete Row';
        }else{
            return 'Remove Row';
        }
    }

    submit(): void {
        this.enableButtons = false;

        // This function saves form to database
        if (this.form.valid) {
            this.updatePinOverrideGroup(this.pinOverrideGroup);
        }

        // Set dates to expected back-end date format
        this.pinOverrideGroup['effectiveDate'] = MenuDateUtil.formatDateToMidnight(this.form.controls['effectiveDate'].value);
        if (this.form.controls['endDate'].value !== 'null' && this.form.controls['endDate'].value !== null) {
            this.pinOverrideGroup['endDate'] = MenuDateUtil.formatDateToMidnight(this.form.controls['endDate'].value);
        }

        const groupWithoutLocationSetType = { // <- this is a temporary solution to not cause backend errors updating a pin override group
            id: this.pinOverrideGroup.id,
            location: this.pinOverrideGroup.location,
            name: this.pinOverrideGroup.name,
            description: this.pinOverrideGroup.description,
            effectiveDate: this.pinOverrideGroup.effectiveDate,
            endDate: this.pinOverrideGroup.endDate,
            pinOverrides: this.updateOverrides(this.pinOverrideGroup),
        };
        if (groupWithoutLocationSetType.pinOverrides) {
            this.managementApiService.savePinOverridesGroup(groupWithoutLocationSetType)
                .pipe(takeUntilDestroyed(this.destroyRef))
                .subscribe({
                    next: () => {
                        this.notificationService.success('Success saving PIN Overrides');
                        this.getPinOverrides();
                        this.dateValidations(this.pinOverrideGroup).then();
                    },
                    error: (error) => {
                        this.notificationService.error(error?.error?.error.message || API_ERRORS.GenericErrorMessage);
                    }
                });
        } else {
            this.notificationService.error('RMI cannot be less than or equal to 0');
        }
    }

    openConfirmRestorePinsDialog(): void {
        const dialogRef = this.dialog.open(ConfirmRestorePinsDialogComponent, {
            width: '350px',
            data: {
                sessionStorage: window.sessionStorage,
                endDate: this.pinOverrideGroup.endDate,
                tomorrow: this.tomorrow,
                apiService: this.managementApiService,
                notificationService: this.notificationService,
                dialog: this.dialog,
                model: this.pinOverrideGroup,

                restore(model: any, endDate: any, tomorrow: any, apiService: ManagementApiService,
                        notificationService: NotificationService, dialog: MatDialog): void {
                    // This function sets endDate to tomorrow at 12 AM
                    endDate = tomorrow;

                    this.pinOverrideGroup = {
                        id: model.id,
                        location: model.location,
                        name: model.name,
                        description: model.description,
                        effectiveDate: MenuDateUtil.formatDateToMidnight(model.effectiveDate),
                        endDate: MenuDateUtil.formatDateToMidnight(endDate),
                    };

                                       for (let i = 0; i < model.pinOverrides.length; i++) {
                        model.pinOverrides[i].rmi = Number(model.pinOverrides[i].rmi);
                        model.pinOverrides[i].primaryOverride = Number(model.pinOverrides[i].primaryOverride);
                        model.pinOverrides[i].secondaryOverride = Number(model.pinOverrides[i].secondaryOverride);
                        model.pinOverrides = model.pinOverrides as PinOverride;
                        this.pinOverrideGroup.pinOverrides = model.pinOverrides;
                    }
                    apiService.savePinOverridesGroup(this.pinOverrideGroup)
                        .pipe(takeUntilDestroyed(this.destroyRef))
                        .subscribe(() => {
                        notificationService.success('Success saving PIN Overrides');
                        this.dialog.closeAll();
                    });
                }
            }
        });
    }

    async dateValidations(pinOverrideGroup: PinOverrideGroup) {
        this.areEditButtonsVisible.emit(true);

        // Create today's date in backend format
        const date = new Date();
        const datePlus1 = new Date(date);
        datePlus1.setDate(datePlus1.getDate() + 1);
        const today = date;
        this.tomorrow = datePlus1.toString();
        this.tomorrow = MenuDateUtil.formatDateToMidnight(this.tomorrow);

        // Grab dates to compare against current date
        const effectiveDate = new Date(pinOverrideGroup.effectiveDate);
        let endDate;

        if (pinOverrideGroup.endDate === undefined) {
            endDate = today;
        } else {
            endDate = pinOverrideGroup.endDate ? new Date(pinOverrideGroup.endDate) : null;
        }

        // Validate based on effective and end date
        if (endDate === null && effectiveDate >= today) {
            this.isRestoreVisible.emit(false);

            // whole form editable
            this.form = new FormGroup({
                locationSetType: new FormControl({value: this.pinOverrideGroup.locationSetType, disabled: false}),
                location: new FormControl({value: this.pinOverrideGroup.location, disabled: false},
                    {nonNullable: true, validators: Validators.required}),
                name: new FormControl({value: this.pinOverrideGroup.name, disabled: false},
                    {nonNullable: true, validators: Validators.required}),
                description: new FormControl ({value: this.pinOverrideGroup.description, disabled: false}),
                effectiveDate: new FormControl ({value: this.pinOverrideGroup.effectiveDate, disabled: false},
                    {nonNullable: true, validators: Validators.required}),
                endDate: new FormControl ({value: this.pinOverrideGroup.endDate, disabled: false},
                    {nonNullable: true}),
                pinOverrides: new FormControl ({
                    value: this.pinOverrideGroup.pinOverrides.filter(result => (result.rmi > 0 &&
                        (result.primaryOverride > 0 || result.secondaryOverride > 0 || result.wholeProductOverride > 0 ||
                            result.samplingDistributionOverride > 0))), disabled: true
                }),
            });

        } else if (endDate === null && effectiveDate <= today) {
            this.isRestoreVisible.emit(false);

            // only end date editable
            this.form = new FormGroup({
                locationSetType: new FormControl({value: this.pinOverrideGroup.locationSetType, disabled: false}),
                location: new FormControl({value: this.pinOverrideGroup.location, disabled: false},
                    {nonNullable: true, validators: Validators.required}),
                name: new FormControl({value: this.pinOverrideGroup.name, disabled: true},
                    {nonNullable: true, validators: Validators.required}),
                description: new FormControl ({value: this.pinOverrideGroup.description, disabled: true}),
                effectiveDate: new FormControl ({value: this.pinOverrideGroup.effectiveDate, disabled: true},
                    {nonNullable: true, validators: Validators.required}),
                endDate: new FormControl ({value: this.pinOverrideGroup.endDate, disabled: false},
                    {nonNullable: true}),
                pinOverrides: new FormControl ({
                    value: this.pinOverrideGroup.pinOverrides.filter(result => (result.rmi > 0 &&
                        (result.primaryOverride > 0 || result.secondaryOverride > 0 || result.wholeProductOverride > 0 ||
                            result.samplingDistributionOverride > 0))), disabled: true
                }),
            });

        }
        else if ((effectiveDate > today) && (endDate > today)) {
            this.isRestoreVisible.emit(false);

            // all fields editable (except location, use button to edit)
            this.form = new FormGroup({
                locationSetType: new FormControl({value: this.pinOverrideGroup.locationSetType, disabled: false}),
                location: new FormControl ({value: this.pinOverrideGroup.location, disabled: false},
                    {nonNullable: true, validators: Validators.required}),
                name: new FormControl ({value: this.pinOverrideGroup.name, disabled: false},
                    {nonNullable: true, validators: Validators.required}),
                description: new FormControl ({value: this.pinOverrideGroup.description, disabled: false}),
                effectiveDate: new FormControl ({value: this.pinOverrideGroup.effectiveDate, disabled: false},
                    {nonNullable: true, validators: Validators.required}),
                endDate: new FormControl ({value: this.pinOverrideGroup.endDate, disabled: false},
                    {nonNullable: true}),
                pinOverrides: new FormControl ({
                    value: this.pinOverrideGroup.pinOverrides.filter(result => (result.rmi > 0 &&
                        (result.primaryOverride > 0 || result.secondaryOverride > 0 || result.wholeProductOverride > 0 ||
                            result.samplingDistributionOverride > 0))), disabled: false
                }),
            });

        } else if ((effectiveDate <= today) && (endDate > today)) {
            this.isRestoreVisible.emit(true);

            // make only end date editable
            this.form = new FormGroup({
                locationSetType: new FormControl({value: this.pinOverrideGroup.locationSetType, disabled: false}),
                location: new FormControl ({value: this.pinOverrideGroup.location, disabled: false},
                    {nonNullable: true, validators: Validators.required}),
                name: new FormControl ({value: this.pinOverrideGroup.name, disabled: true},
                    {nonNullable: true, validators: Validators.required}),
                description: new FormControl ({value: this.pinOverrideGroup.description, disabled: true}),
                effectiveDate: new FormControl ({value: this.pinOverrideGroup.effectiveDate, disabled: true},
                    {nonNullable: true, validators: Validators.required}),
                endDate: new FormControl ({value: this.pinOverrideGroup.endDate, disabled: false}, {nonNullable: true}),
                pinOverrides: new FormControl ({
                    value: this.pinOverrideGroup.pinOverrides.filter(result => (result.rmi > 0 &&
                        (result.primaryOverride > 0 || result.secondaryOverride > 0 || result.wholeProductOverride > 0 ||
                            result.samplingDistributionOverride > 0))), disabled: true
                }),
            });
        } else if ((endDate <= today)) {
            this.isRestoreVisible.emit(false);

            // make all fields view only
            this.form = new FormGroup({
                locationSetType: new FormControl({value: this.pinOverrideGroup.locationSetType, disabled: false}),
                location: new FormControl ({value: this.pinOverrideGroup.location, disabled: false},
                    {nonNullable: true, validators: Validators.required}),
                name: new FormControl ({value: this.pinOverrideGroup.name, disabled: true},
                    {nonNullable: true, validators: Validators.required}),
                description: new FormControl ({value: this.pinOverrideGroup.description, disabled: true}),
                effectiveDate: new FormControl ({value: this.pinOverrideGroup.effectiveDate, disabled: true},
                    {nonNullable: true, validators: Validators.required}),
                endDate: new FormControl ({value: this.pinOverrideGroup.endDate, disabled: true}, {nonNullable: true}),
                pinOverrides: new FormControl ({
                    value: this.pinOverrideGroup.pinOverrides.filter(result => (result.rmi > 0 &&
                        (result.primaryOverride > 0 || result.secondaryOverride > 0 || result.wholeProductOverride > 0 ||
                            result.samplingDistributionOverride > 0))), disabled: true
                }),
            });
        }
        if (!this.canEdit) {
            this.form.disable();
        }
        this.form.controls['location'].setValue(this.pinOverrideGroup.location);
    }

    publishAllPins(forceOverride?: boolean, location?: string) {
        return this.managementApiService.publishAllPins(forceOverride, location);
    }
}
